import {
  Graphic,
  CreateGraphicRequest,
  CreateGraphicFromFileIdRequest,
  CreateGraphicFromDataStreamURIRequest,
  UpdateGraphicRequest,
  AnyEditionDTO,
  User,
  LocationWithPath,
  TopicCodeWithSlugline,
  GraphicDTO,
  PublishingLocationExpandedDTO,
} from '@graphics/gfx-common/dist/types'
import { Language } from '@graphics/gfx-common/dist/constants'
import {
  IEditionMetadata,
  IPreviewImages,
  IEditionMetadataInput,
  IEditionPublishing,
  PackType,
  IPackInput,
  IPromotedTo,
} from '../types/pack'
import { IContinuationResponse } from '../types/continuationScroll'
import { IHistoryEvent } from '../types/history'
import { IRevision } from '../components/RevisionsList/RevisionsList'
import { IPublishedGraphic } from '../types/publishedGraphic'
import { ITask } from '../types/polling'
import {
  baseUrl,
  safePost,
  safeGet,
  safePut,
  safeDelete,
  safeGetWithModified,
} from './http'
import { matchId } from './common'

export const packUrl = `${baseUrl}/graphics`

export const editionsUrl = (packId: string): string =>
  `${packUrl}/${packId}/editions`

const transformToCreateGraphicRequestData = ({
  id,
  created,
  createdBy,
  modifiedBy,
  ...pack
}: PackType): CreateGraphicRequest => ({
  ...pack,
  language: pack.language as Language,
  location: pack.location as LocationWithPath,
  topicCodes: pack.topicCodes as TopicCodeWithSlugline[],
})

export const createPack = (packData: PackType): Promise<Graphic> =>
  safePost<CreateGraphicRequest>(
    packUrl,
    transformToCreateGraphicRequestData(packData)
  )

export const createPackFromFile = (
  group: string,
  fileId: string
): Promise<ITask> =>
  safePost<CreateGraphicFromFileIdRequest>(packUrl, { group, fileId })

export const createPackFromDataStreamURI = (
  group: string,
  dataStreamURI: string
): Promise<ITask> =>
  safePost<CreateGraphicFromDataStreamURIRequest>(packUrl, {
    group,
    dataStreamGraphic: { dataStreamURI },
  })

export const getPackById = (
  id: string,
  modified?: string | false
): Promise<GraphicDTO> =>
  modified
    ? safeGetWithModified(`${packUrl}/${id}`, modified)
    : safeGet(`${packUrl}/${id}`)

export const transformStateToUpdateGraphicRequest = (
  pack: PackType
): UpdateGraphicRequest => ({
  ...pack,
  language: pack.language as Language,
  location: pack.location as LocationWithPath,
  topicCodes: pack.topicCodes as TopicCodeWithSlugline[],
  canDelete: !!pack.canDelete,
  editions: pack.editions as AnyEditionDTO[],
  modified: pack.modified as string,
  modifiedDate: pack.modified as string,
  modifiedBy: pack.modifiedBy as User,
  editors: pack.editors as User[],
})

export const updatePack = (packData: PackType): Promise<ITask> =>
  safePut<UpdateGraphicRequest>(
    `${packUrl}/${packData.id}`,
    transformStateToUpdateGraphicRequest(packData)
  )

export const deletePack = (id: string): Promise<ITask> =>
  safeDelete(`${packUrl}/${id}`)

export const deleteFile = (packId: string, fileId: string): Promise<ITask> =>
  safeDelete(`${packUrl}/${packId}/editions/${fileId}`)

export const createEditionFromFile = (
  packId: string,
  fileData: { fileId: string }
): Promise<any> => safePost(editionsUrl(packId), fileData)

export const updateFileInEdition = (
  packId: string,
  fileData: { fileId: string; editions: string[]; modified: string }
): Promise<any> => safePut(editionsUrl(packId), fileData)

export const getPackHistory = (
  packId?: string,
  editionId?: string,
  token?: string
): Promise<IContinuationResponse<IHistoryEvent>> => {
  if (!packId) {
    return Promise.reject('There is no such pack')
  }
  return safeGet(
    `${packUrl}/${packId}/${
      editionId ? `editions/${editionId}/` : ''
    }history?take=20${token ? `&continuationToken=${token}` : ''}`
  )
}

export const getRevisions = (
  revisionGroupId?: string,
  token?: string
): Promise<IContinuationResponse<IRevision>> =>
  safeGet(
    `${baseUrl}/file-revisions/${revisionGroupId}?take=20${
      token ? `&continuationToken=${token}` : ''
    }`
  )

export const updatePublishOptions = ({
  publishing,
  isLynxSearchable,
  publishingLocations,
}: IEditionMetadataInput): IEditionPublishing => ({
  ...publishing,
  locations: publishing.locations.map(
    (
      location: PublishingLocationExpandedDTO
    ): PublishingLocationExpandedDTO => ({
      ...location,
      publish:
        publishingLocations && publishingLocations.includes(location.name),
    })
  ),
  promote: isLynxSearchable,
})

export const concatenate = (
  pack: PackType,
  edition: IEditionMetadata | IEditionMetadataInput,
  fieldName: string
): string => {
  const packField: string = (pack[fieldName] ?? '').trim()
  const editionField: string = (edition[fieldName] ?? '').trim()
  const isSlugLine = fieldName === 'slugline'
  const delimeter = isSlugLine && packField.endsWith('/') ? '' : ' '
  const concatenated = `${packField}${delimeter}${editionField}`.trim()
  const isLanguageEqual =
    !edition.language || edition.language === pack.language
  const shouldConcatenate =
    isLanguageEqual || (isSlugLine && edition.addToPackSlug)

  return shouldConcatenate ? concatenated : editionField
}

export const concatenateGraphicField = (
  field: string,
  edition: IEditionMetadata | IPublishedGraphic,
  pack: PackType,
  delimiter: string = ' '
): string =>
  edition.language === null || edition.language === pack.language
    ? `${pack[field] || ''}${delimiter}${edition[field] || ''}`.trim()
    : edition[field] || ''

export const getMatchedEdition = (
  editions: IEditionMetadata[] | undefined,
  fileName: string | null
): IEditionMetadata | undefined => {
  if (!fileName || !editions) {
    return undefined
  }
  const filename = fileName.toLowerCase()
  return editions
    .filter((edition) => !!edition.file)
    .find((edition) => edition.file.fileName.toLowerCase() === filename)
}

export const extractEditionById = (editionId?: string) => (
  pack?: PackType
): IEditionMetadata | undefined =>
  pack && pack.editions && pack.editions.find(matchId(editionId))

export const prohibitedTab = (tab?: string, edition?: IEditionMetadata) =>
  tab === 'revisions' &&
  !['GraphicFile', 'GraphicFolder'].includes(
    (edition && edition.editionType) || ''
  )

export const createEditionAndPackData = (pack: PackType, edition) => {
  const title = edition ? concatenate(pack, edition, 'title') : pack.title
  const caption = edition
    ? concatenate(pack, edition, 'description')
    : pack.description
  const slugline = edition
    ? concatenate(pack, edition, 'slugline')
    : pack.slugline

  return {
    title,
    caption,
    slugline,
  }
}

export type DescriptionArrayType = Array<{
  label: string
  key: string
  shortName?: string
}>

export const createPanelPackContent = (
  pack: PackType
): Array<{
  titleName: string
  description?: DescriptionArrayType | string
}> => [
  {
    titleName: 'Tags',
    description: pack.tags.map(({ text }) => ({ label: text, key: text })),
  },
  {
    titleName: 'Topics',
    description: pack.topicCodes.map(({ n2000, rcsId, shortName }) => ({
      label: (n2000 && n2000[0]) || rcsId,
      key: rcsId,
      shortName,
    })),
  },
  {
    titleName: "Editor's location",
    description: pack.location?.shortName,
  },
  {
    titleName: 'Contact',
    description: pack.contacts.map(({ name, id }) => ({
      label: name,
      key: id,
    })),
  },
]

export const checkIfEditionIsDeleted = ({ file }: IEditionMetadata): boolean =>
  file && file.deleted

export const checkIfSomeEditionIsDeleted = (
  editions: IEditionMetadata[]
): boolean => !!editions && editions.some(checkIfEditionIsDeleted)

export const resolvePublishedURL = async (
  { packId, editionId, name },
  onSuccess: (url: string) => void,
  onError: (message: string) => void
) => {
  try {
    const { publishedURI } = await safeGet(
      `${baseUrl}/graphics/${packId}/editions/${editionId}/publishing-locations/${name}`
    )
    onSuccess(publishedURI)
  } catch (err) {
    onError(err.message)
  }
}

export const getFirstEditionIdFromPack = ({ editions }) =>
  editions[0] ? editions[0].id : 'overall-pack'

export const getPreviewImages = ({
  previewImages,
}:
  | IPackInput
  | PackType
  | IEditionMetadataInput
  | IEditionMetadata): IPreviewImages =>
  previewImages || {
    thumbnailURI: undefined,
    previewURI: undefined,
    newTmpPreview: undefined,
    newFileId: undefined,
    fileId: null,
  }

export const getPromoDestinations = (edition): IPromotedTo => ({
  LynxEvents: !!edition.publishing.promoDestinations.find(
    ({ name }) => name === 'LynxEvents'
  )?.promoted,
  Portal: !!edition.publishing.promoDestinations.find(
    ({ name }) => name === 'Portal'
  )?.promoted,
})
